import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import logger from '../../../../../utils/logger';
import axiosInstance from '../../../../../fetchConfig/axiosConfig';

// Matches the `state` the /printers route returns.
const PRINTER_STATUS = {
  ready: { color: '#2E9E5B', label: 'Ready' },
  busy: { color: '#2E9E5B', label: 'Printing' },
  warning: { color: '#C98A00', label: 'Needs attention' },
  error: { color: '#C0392B', label: 'Not ready' },
  offline: { color: '#9AA4B2', label: 'Not responding' },
  unknown: { color: '#9AA4B2', label: 'Status unknown' },
};

const statusOf = (p) => PRINTER_STATUS[p?.state] || PRINTER_STATUS.unknown;

// Loads printers + live status whenever the dialog opens. The last pick is kept in
// localStorage.printerId; an unknown or missing id falls back to the server default.
export function usePrinters(open) {
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [printerId, setPrinterIdState] = useState(() => localStorage.getItem('printerId'));

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axiosInstance.get('/printers')
      .then(({ data }) => setPrinters(Array.isArray(data) ? data : []))
      .catch(logger.error)
      .finally(() => setLoading(false));
  }, [open]);

  const printer = useMemo(
    () => printers.find((p) => p.id === printerId) || printers.find((p) => p.default) || printers[0] || null,
    [printers, printerId],
  );

  const setPrinterId = useCallback((id) => {
    setPrinterIdState(id);
    localStorage.setItem('printerId', id);
  }, []);

  return { printers, printer, loading, setPrinterId };
}

const Dot = ({ printer }) => (
  <span aria-hidden="true" className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: statusOf(printer).color }} />
);

const describe = (p) => [p.detail || statusOf(p).label, p.staples === false && 'no stapler'].filter(Boolean).join(', ');

export default function PrinterPicker({ printers, printer, loading, setPrinterId, disabled }) {
  const [anchor, setAnchor] = useState(null);
  const tip = printer ? `${printer.name} (${describe(printer)})` : loading ? 'Checking printers…' : 'Using the default printer';

  return (
    <>
      <Tooltip title={tip} disableInteractive>
        <span>
          <Button
            variant="outlined"
            disabled={disabled || !printers.length}
            startIcon={<PrintOutlinedIcon sx={{ fontSize: 18 }} />}
            endIcon={<ArrowDropDownIcon />}
            aria-haspopup="menu"
            aria-expanded={Boolean(anchor)}
            aria-label={`Printer: ${tip}`}
            onClick={(e) => setAnchor(e.currentTarget)}
            className="rounded-md border-[#D5DCE3] font-semibold normal-case text-[#17212B]"
          >
            <span className="inline-flex min-w-0 items-center gap-2">
              {printer && <Dot printer={printer} />}
              <span className="max-w-[130px] truncate">{printer?.name || (loading ? 'Printers…' : 'Default printer')}</span>
              {loading && <CircularProgress size={10} sx={{ color: '#5C6B7A' }} />}
            </span>
          </Button>
        </span>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: 260 } } }}
      >
        {printers.map((p) => (
          <MenuItem key={p.id} selected={p.id === printer?.id} onClick={() => { setPrinterId(p.id); setAnchor(null); }}>
            <span className="flex w-full items-start gap-2.5">
              <span className="mt-[7px]"><Dot printer={p} /></span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-[#17212B]">{p.name}</span>
                <span className="block text-xs text-[#5C6B7A]">{describe(p)}</span>
              </span>
            </span>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
